import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import { LocationOn, Phone, Email, Language, WhatsApp } from "@mui/icons-material";
import { Helmet } from "react-helmet";

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleEmailSend = () => {
    const subject = encodeURIComponent("Inquiry from Namami India Website");
    const body = encodeURIComponent(
      `Hello Namami India,\n\nMy name is ${formData.name}.\nEmail: ${formData.email}\n\nMessage:\n${formData.message}\n\nRegards,\n${formData.name}`
    );
    window.location.href = `mailto:info@namamiindia.co.in?subject=${subject}&body=${body}`;
  };

  const handleWhatsAppSend = () => {
    const number = "+919692783839";
    const message = encodeURIComponent(
      `Hello Namami India,\n\nMy name is ${formData.name}.\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`
    );
    window.open(`https://wa.me/${number}?text=${message}`, "_blank");
  };

  return (
    <>
      <Helmet>
        <title>Contact Us - Namami India Support Services</title>
        <meta
          name="description"
          content="Reach out to Namami India Support Services for facility management, hospitality, and maintenance solutions across India. Let's build cleaner, safer, and smarter spaces together."
        />
        <meta
          name="keywords"
          content="Namami India contact, facility management support, hospitality services contact, Odisha, Bhubaneswar"
        />
        <meta property="og:title" content="Contact Namami India Support Services" />
        <meta
          property="og:description"
          content="Get in touch with Namami India’s expert team for integrated facility and hospitality services. We’re just a message away."
        />
        <meta property="og:image" content="https://www.namamiindia.co.in/assets/namami-logo.png" />
        <meta property="og:url" content="https://www.namamiindia.co.in/contact" />
        <link rel="canonical" href="https://www.namamiindia.co.in/contact" />
      </Helmet>

      {/* === Hero Section === */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #003366 0%, #004aad 100%)",
          color: "white",
          py: { xs: 10, md: 14 },
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Typography
          variant="h3"
          fontWeight={800}
          sx={{ mb: 2, letterSpacing: "0.5px", textShadow: "0 3px 6px rgba(0,0,0,0.3)" }}
        >
          Get in Touch with Namami India
        </Typography>
        <Typography
          variant="h6"
          maxWidth="800px"
          sx={{
            lineHeight: 1.7,
            opacity: 0.95,
            px: { xs: 3, md: 0 },
          }}
        >
          Let’s collaborate to make spaces smarter, cleaner, and more sustainable. Whether you need
          comprehensive facility management, hospitality support, or specialized maintenance
          solutions — our team is ready to help across India. Reach out today to experience the
          Namami India difference.
        </Typography>
      </Box>

      {/* === About / Intro Paragraph === */}
      <Container sx={{ py: { xs: 6, md: 10 }, textAlign: "center" }}>
        <Typography
          variant="h4"
          fontWeight={800}
          gutterBottom
          sx={{
            color: "primary.main",
          }}
        >
          Why Choose Namami India?
        </Typography>
        <Typography
          variant="body1"
          sx={{
            maxWidth: "900px",
            mx: "auto",
            color: "text.secondary",
            lineHeight: 1.9,
            fontSize: "1.05rem",
            mb: 5,
          }}
        >
          At Namami India Support Services Pvt. Ltd., we believe every environment deserves the
          highest standard of care. From corporate facilities and educational institutions to
          hospitality spaces and residential complexes, our expertise extends across every sector.
          Our approach combines human excellence, advanced technology, and a commitment to hygiene,
          safety, and sustainability. Each project we undertake is not just about maintenance — it's
          about transforming everyday spaces into inspiring places where people feel comfortable,
          productive, and valued.
          <br />
          <br />
          Our dedicated support team is always just a call or message away. Whether you need a quick
          service inquiry, a customized quotation, or ongoing facility management support, we ensure
          timely communication and unmatched professionalism.
        </Typography>
      </Container>

      {/* === Contact + Form Section === */}
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          backgroundColor: "#f9fafb",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Container
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "stretch",
            justifyContent: "center",
            gap: { xs: 5, md: 8 },
          }}
        >
          {/* === Contact Info === */}
          <Card
            sx={{
              flex: 1,
              p: { xs: 3, sm: 4 },
              background: "rgba(255,255,255,0.9)",
              borderRadius: 4,
              backdropFilter: "blur(8px)",
              boxShadow: "none",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              "&:hover": { transform: "translateY(-3px)" },
              transition: "all 0.3s ease",
            }}
          >
            <Typography
              variant="h4"
              fontWeight={800}
              mb={3}
              sx={{
                background: "linear-gradient(90deg, #1565c0, #004ba0)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                textAlign: { xs: "center", md: "left" },
              }}
            >
              Contact Us
            </Typography>

            <Box display="flex" alignItems="flex-start" mb={2}>
              <LocationOn sx={{ color: "primary.main", mr: 1.5, mt: 0.5 }} />
              <Typography color="text.secondary" lineHeight={1.7}>
                Behind Gowrishankar English Medium School, Plot No - 239, Rudra Vihar, 
                Sishupal Garh, Dhauli, Bhubaneswar, Odisha – 751002
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" mb={1.5}>
              <Phone sx={{ color: "primary.main", mr: 1.5 }} />
              <Typography color="text.secondary">
                +91 96927 83839, +91 89176 29368, +91 98401 10545
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" mb={1.5}>
              <Email sx={{ color: "primary.main", mr: 1.5 }} />
              <Typography color="text.secondary">info@namamiindia.co.in</Typography>
            </Box>

            <Box display="flex" alignItems="center" mb={3}>
              <Language sx={{ color: "primary.main", mr: 1.5 }} />
              <Typography color="text.secondary">www.namamiindia.co.in</Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box display="flex" gap={2} flexWrap="wrap" mt={2}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Email />}
                onClick={handleEmailSend}
                sx={{
                  textTransform: "none",
                  borderRadius: 3,
                  px: 3,
                  flex: 1,
                }}
              >
                Send Email
              </Button>
              <Button
                variant="outlined"
                color="success"
                startIcon={<WhatsApp />}
                onClick={handleWhatsAppSend}
                sx={{
                  textTransform: "none",
                  borderRadius: 3,
                  px: 3,
                  flex: 1,
                  borderWidth: 2,
                  "&:hover": {
                    borderWidth: 2,
                    backgroundColor: "rgba(76,175,80,0.1)",
                  },
                }}
              >
                WhatsApp Us
              </Button>
            </Box>
          </Card>

          {/* === Message Form === */}
          <Card
            sx={{
              flex: 1,
              p: { xs: 3, sm: 4 },
              background: "rgba(255,255,255,0.95)",
              borderRadius: 4,
              boxShadow: "none",
              "&:hover": { transform: "translateY(-3px)" },
              transition: "all 0.3s ease",
            }}
          >
            <CardContent sx={{ p: 0 }}>
              <Typography
                variant="h5"
                fontWeight={700}
                mb={2}
                sx={{
                  color: "primary.main",
                  textAlign: { xs: "center", md: "left" },
                }}
              >
                Send us a Message
              </Typography>
              <TextField
                fullWidth
                label="Your Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Your Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Your Message"
                name="message"
                multiline
                rows={4}
                value={formData.message}
                onChange={handleChange}
                margin="normal"
              />

              <Box
                display="flex"
                gap={2}
                mt={3}
                flexWrap="wrap"
                justifyContent={{ xs: "center", md: "flex-start" }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleEmailSend}
                  sx={{
                    textTransform: "none",
                    borderRadius: 3,
                    px: 4,
                    py: 1.2,
                  }}
                >
                  Send via Email
                </Button>
                <Button
                  variant="outlined"
                  color="success"
                  onClick={handleWhatsAppSend}
                  startIcon={<WhatsApp />}
                  sx={{
                    textTransform: "none",
                    borderRadius: 3,
                    px: 4,
                    py: 1.2,
                    borderWidth: 2,
                    "&:hover": {
                      borderWidth: 2,
                      backgroundColor: "rgba(76,175,80,0.1)",
                    },
                  }}
                >
                  Send via WhatsApp
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>
    </>
  );
}
